(ns minorodata.components.ai-chat.core
  (:require
   ["allotment" :refer [Allotment]]
   ["date-fns" :refer [differenceInSeconds]]
   ["humanize-duration" :as humanizeDuration]
   ["lucide-react" :refer [Anchor ChartColumn ChartNoAxesCombined Check ChevronRight
                           FilePlus2 LayoutList Loader2 Menu Play Plug Plus Save
                           SearchCode Send ChevronDown BrushCleaning ServerCog
                           MessageCircleCode Square SquareLibrary BarChart2 ClipboardCopy
                           Copy Unplug X]]
   ["react-apexcharts" :default ApexChart]
   ["smooth-scroll-into-view-if-needed" :as scroll-into-view]
   ["use-elapsed-time" :refer [useElapsedTime]]
   [minorodata.app.api-config :refer [API_HOST]]

   [applied-science.js-interop :as jsi]
   [bleene.dag.context-menu :refer [launch-transform]]
   [clojure.string :as clojure.string]
   [goog.functions :refer [throttle]]
   [goog.string :as gstring]
   [minorodata.app.config :refer [slow-query-duration-s]]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.app.posthog-events :as posthog-events]
   [minorodata.app.utils :as app.utils]
   [minorodata.components.dag.utils :refer [go-to-dag]]
   [minorodata.components.monaco.completions :as monaco.completions]
   [minorodata.components.monaco.editor :as monaco.editor]
   [minorodata.components.spinner.core :as c.spinner]
   [minorodata.components.sql-console-modal.utils :refer [close-tab tab-id]]
   [minorodata.components.sql-console.core :as c.sql-console]
   [minorodata.components.sql-console.data-preview :as c.data-preview]
   [minorodata.components.sql-console.sidebar :as c.sql-console-sidebar]
   [minorodata.components.transformations.transforms :refer [revert-circular-dependency-modal
                                                             versions-list-modal]]
   [minorodata.components.transformations.utils :refer [go-to-logs
                                                        move-transform
                                                        move-transform-submenu
                                                        show-versions-history]]
   [minorodata.ai-chat.byol :as byol]
   [minorodata.ai-chat.confirmation :as confirmation]
   [minorodata.ai-chat.response :as response]
   [minorodata.components.ai-chat.model-control :refer [kai-default-notice model-control]]
   [minorodata.components.markdown.core :refer [formatted-text]]
   [minorodata.sql-console.model :refer [->sql-editor]]
   [minorodata.utils :as utils]
   [mold-forms.core :as form-component]
   [mold-forms.model :refer [->form-model]]
   [re-frame.core :as re-frame]
   [re-posh.core :as re-posh]
   [react :as react]
   [react-hotkeys-hook :refer [useHotkeys]]
   [reagent.core :as reagent]
   [reitit.frontend.easy :as rt.easy]
   [ui.button :refer [button]]
   [ui.chip]
   [ui.data-grid.preview :as data-grid-preview]
   [ui.dialog :refer [dialog-actions dialog-content dialog-root dialog-title]]
   [ui.dropdown-menu :refer [dropdown-menu-content dropdown-menu-item
                             dropdown-menu-root dropdown-menu-separator
                             dropdown-menu-sub dropdown-menu-trigger]]
   [ui.fields :refer [input]]
   [ui.modal]
   [ui.shortcut]
   [ui.tabs]
   [ui.tooltip :refer [tooltip]]
   [ui.typography]
   [ui.utils :refer [clsx]]
   [oxbow.re-frame :as o]))
   ; [oxbow.core :as o]


(defn open-ai-modal []
  (dispatch [:modals.events/toggle-modal "ai-modal" true]))

(defn toggle-ai-modal []
  (let [open? @(re-frame/subscribe [:modals.subs/modal-open? "ai-modal"])]
    (dispatch [:modals.events/toggle-modal "ai-modal" (not open?)])))

(defn- context-badge [{:keys [name on-clear]}]
  (when name
    [:div {:class ["flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 min-w-0"]}
     [:> SquareLibrary {:size 13 :class "shrink-0"}]
     [:span {:class "truncate min-w-0" :title (str "Transform: " name)} name]
     (when on-clear
       [:button {:type "button"
                 :aria-label "Clear context"
                 :class "shrink-0 rounded-full p-0.5 hover:bg-slate-100 dark:hover:bg-slate-800"
                 :on-click on-clear}
        [:> X {:size 12}]])]))

(defn- reset-input-height []
  (when-let [el (js/document.getElementById "ai-chat")]
    (jsi/assoc-in! el [:style :height] "auto")))

(defn strip-context [message]
  (-> message
      (clojure.string/replace #"<CONTEXT>[\s\S]*?</CONTEXT>" "")
      (clojure.string/replace #"^Approved\. Apply the proposal verbatim:[\s\S]*$" "✓ Approved")))

(defn user-message [{:keys [text]}]
  [:div {:class "space-y-4 w-full relative max-w-[42rem] pt-3 pb-3 px-2"}
   [:div
    {:class "flex gap-3 justify-end"}
    [:div
     {:class
      "rounded-lg p-2 text-foreground bg-secondary prose text-sm break-words whitespace-normal prose-h2:mt-0! prose-h2:scroll-m-0! dark:prose-invert"}
     [formatted-text {:text (strip-context text)}]]

    #_[:span
       {:class
        "shrink-0 inline-flex size-10 items-center justify-center rounded-full bg-gray-500 outline -outline-offset-1 outline-black/5 dark:bg-gray-800 dark:outline-white/10"}
       [:span {:class "font-medium text-white"} "YOU"]]]])


(defn manager-message [{:keys [text]}]
  [:div {:class "space-y-4 w-full relative max-w-[42rem] pt-3 pb-3 px-2"}
   [:div
    {:class "flex gap-3"}
    #_[:span
       {:class
        "shrink-0 inline-flex size-10 items-center justify-center rounded-full bg-gray-500 outline -outline-offset-1 outline-black/5 dark:bg-gray-800 dark:outline-white/10"}
       [:span {:class "font-medium text-white"} "KAI"]]

    [:div
     {:class
      "rounded-lg p-2 text-foreground bg-secondary prose text-sm break-words whitespace-normal prose-h2:mt-0! prose-h2:scroll-m-0! dark:prose-invert overflow-auto"}
     [formatted-text {:text (response/strip-proposal-block text)}]]]])

(defn- copy-response-button [_]
  (let [copied? (reagent/atom false)]
    (fn [{:keys [text]}]
      [:div {:class "w-full max-w-[42rem] px-2 -mt-2"}
       [:button {:type       "button"
                 :class      "rounded-md p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                 :title      (if @copied? "Copied" "Copy response")
                 :aria-label (if @copied? "Copied" "Copy response")
                 :on-click   (fn []
                               (dispatch [:app.events/copy-to-clipboard text false])
                               (reset! copied? true)
                               (js/setTimeout #(reset! copied? false) 2000))}
        (if @copied?
          [:> Check {:size 15}]
          [:> Copy {:size 15}])]])))

(def tools
  {"get_table_data_preview_tool"   "Fetching table data preview"
   "get_time"                      "Getting current time"
   "rag_search_tool"               "Searching docs"
   "get_groups_tool"               "Listing groups"
   "get_transforms_tool"           "Listing transforms"
   "get_latest_transform_tool"     "Getting latest transform data"
   "get_transform_name_tool"       "Looking up transform"
   "get_transform_logs_tool"       "Searching logs"
   "search_groups_by_name_tool"    "Searching groups"
   "search_transforms_by_name_tool" "Searching transforms by name"
   "search_transforms_by_sql_tool" "Searching transforms by SQL"
   "get_table_schema_tool"         "Fetching table metadata"
   "search_warehouse_tables_tool"  "Searching warehouse tables"
   "get_recently_failed_jobs_tool" "Checking recent failures"
   "build_chart_tool"              "Building chart"
   "create_sandbox_transform_tool" "Creating transform in Sandbox"
   "edit_sandbox_transform_tool"   "Updating transform in Sandbox"
   "list_tags_tool"                "Listing tags"
   "get_entity_tags_tool"          "Fetching tags"
   "add_tag_tool"                  "Adding tag"
   "remove_tag_tool"               "Removing tag"
   "search_entities_by_tag_tool"   "Searching sources by tag"
   "rename_tag_tool"               "Preparing tag rename"
   "delete_tag_tool"               "Preparing tag deletion"
   "merge_tags_tool"               "Preparing tag merge"})

(defonce kai-charts (atom []))
(defonce kai-batch  (atom 0))

(def ^:private suggestion-sets
  {;; KAI asked user a clarifying question — help them respond
   :needs-info [["🗂️" "Show my available tables"]
                ["📋" "List my transforms"]
                ["🔍" "Search warehouse tables"]]

   ;; A chart was actually rendered
   :chart-done [["📋" "Download as PDF report" :download-pdf]
                ["📈" "Generate another chart"]
                ["💬" "Explain these results"]]

   :sandbox-applied-elsewhere [["🔗" "View in Pipeline Editor → Sandbox" :view-in-pe]]

   ;; Error / failure / logs
   :error      [["🔍" "What's causing this error?"]
                ["🔧" "How do I fix this?"]
                ["📋" "Show all failures today"]]

   ;; Transform SQL / query discussed
   :transform  [["📊" "Chart this transform's output"]
                ["📝" "Document this transform"]
                ["🔧" "Suggest SQL improvements"]]

   ;; Table schema / data preview shown
   :schema     [["📊" "Generate a chart from this table"]
                ["🔧" "Write a transform using this table"]
                ["👁️" "Preview the data"]
                ["📊" "Generate a report from this table"]]

   :default    [["📊" "Generate a chart from my data"]
                ["🔧" "Create a new SQL transform"]
                ["📋" "Build a report"]
                ["📝" "Document my transforms"]]})

(defn- pick-suggestions [text route-name]
  (let [t (or text "")
        has-chart? (seq @kai-charts)
        table-this-turn? (some #(and (= (:batch %) @kai-batch)
                                     (contains? #{"table" "metric"} (:chart_type %)))
                               @kai-charts)]
    (cond
      (re-find #"(?i)\b(created|updated)\b[\s\S]{0,200}\bsandbox\b" t)
      (when (or (not= route-name :pipeline-editor)
                @(re-frame/subscribe [:sql-console-modal.subs/modal-open?]))
        (:sandbox-applied-elsewhere suggestion-sets))

      ;; KAI is asking the user to provide more info
      (re-find #"(?i)(what|which|could you|please).{0,80}(table|transform|name|query|tell me|provide|specify)" t)
      (:needs-info suggestion-sets)

      ;; A real chart exists and the response references it
      (and has-chart? (re-find #"(?i)chart" t))
      (:chart-done suggestion-sets)

      table-this-turn?
      (:chart-done suggestion-sets)

      ;; Error / failure / log content
      (re-find #"(?i)\b(error|failure|failed|broken|log)\b" t)
      (:error suggestion-sets)

      ;; Table schema / preview content
      (re-find #"(?i)(schema|column|preview|warehouse table)" t)
      (:schema suggestion-sets)

      ;; Transform SQL / query content
      (re-find #"(?i)\b(SQL|transform|query)\b" t)
      (:transform suggestion-sets)

      :else
      (:default suggestion-sets))))

(defn- fmt-num [n]
  (when (and n (not (js/isNaN n)))
    (.toLocaleString (js/Number n) js/undefined #js {:maximumFractionDigits 2})))

(defn- fmt-compact [n]
  (when (and n (not (js/isNaN n)))
    (let [num (js/Number n)
          abs (js/Math.abs num)
          fix (fn [v d] (.replace (.toFixed v d) #"\.0$" ""))]
      (cond
        (>= abs 1e9) (str (fix (/ num 1e9) 1) "B")
        (>= abs 1e6) (str (fix (/ num 1e6) 1) "M")
        (>= abs 1e3) (str (fix (/ num 1e3) 1) "K")
        :else (.toLocaleString num js/undefined #js {:maximumFractionDigits 2})))))

(defn- fmt-cell [v]
  (cond
    (nil? v)    "–"
    (number? v) (or (fmt-num v) "–")
    :else       (str v)))

(def ^:private month-abbr
  ["Jan" "Feb" "Mar" "Apr" "May" "Jun" "Jul" "Aug" "Sep" "Oct" "Nov" "Dec"])

(defn- fmt-date-series
  "Convert a vector of x-axis category strings.
   Recognises ISO dates (YYYY-MM-...) and compact YYYYMMDD integers.
   Formats intelligently based on the span of the series."
  [raw-cats]
  (let [yyyymmdd? #(re-find #"^\d{8}$" (str %))
        iso?      #(or (re-find #"^\d{4}-\d{2}" (str %)) (yyyymmdd? %))
        parse-dt  #(if (yyyymmdd? %)
                     (let [s (str %)]
                       (js/Date. (str (subs s 0 4) "-" (subs s 4 6) "-" (subs s 6 8))))
                     (js/Date. (str %)))
        dates     (keep #(when (iso? %) (parse-dt %)) raw-cats)]
    (if (>= (count dates) 2)
      (let [ms     (map #(.getTime %) dates)
            min-ms (apply min ms)
            max-ms (apply max ms)
            span   (/ (- max-ms min-ms) 86400000)
            multi? (not= (.getFullYear (js/Date. min-ms))
                         (.getFullYear (js/Date. max-ms)))
            fmt-d  (fn [d]
                     (let [mo (month-abbr (.getMonth d))
                           yr (.getFullYear d)]
                       (cond
                         (< span 2)    (str (.getHours d) ":" (when (< (.getMinutes d) 10) "0") (.getMinutes d))
                         (<= span 60)  (str (.getDate d) " " mo)
                         (not multi?)  mo
                         (<= span 800) (str mo " " (.slice (.toString yr) 2))
                         :else         (str yr))))]
        (mapv #(if-let [d (when (iso? %) (parse-dt %))]
                 (fmt-d d)
                 (str %))
              raw-cats))
      raw-cats)))

(defn- repeated-colors [n color]
  (vec (repeat (max 1 n) color)))

(defn- ellipsize [s n]
  (let [s (str s)]
    (if (> (count s) n)
      (str (subs s 0 (max 0 (- n 1))) "…")
      s)))

(defn- label-metrics [labels]
  (let [labels    (mapv #(str %) labels)
        count-lbl (count labels)
        lengths   (mapv count labels)]
    {:count     count-lbl
     :max-len   (apply max 0 lengths)
     :total-len (reduce + 0 lengths)
     :avg-len   (if (pos? count-lbl)
                  (/ (reduce + 0 lengths) count-lbl)
                  0)}))

(defn- choose-bar-layout [labels]
  (let [{:keys [count max-len total-len]} (label-metrics labels)]
    (cond
      (or (> count 8)
          (> max-len 16)
          (> total-len 52))
      :horizontal

      (or (> count 5)
          (> max-len 10)
          (> total-len 30))
      :vertical-rotated

      :else
      :vertical)))

(declare download-report!)

(defn- chip-handler
  [action label {:keys [master-id sql] :as transform} send-message]
  (case action
    :download-pdf #(download-report!)

    :view-in-pe   #(if master-id
                     (dispatch [:ai-chat/navigate-to-pe-with-tab transform])
                     (do (dispatch [:sql-console-modal.events/toggle-modal false])
                         (dispatch [:app.events/navigate :pipeline-editor])))

    #(send-message label)))

(defn- suggestion-chips [{:keys [send-message last-text created-transform]}]
  (let [route-name (:app-route/name @(re-frame/subscribe [:app-route/pull]))]
    [:div {:class "flex flex-wrap gap-2 px-3 py-2 max-w-[42rem]"}
     (for [chip (pick-suggestions last-text route-name)
           :let [icon   (nth chip 0)
                 label  (nth chip 1)
                 action (nth chip 2 nil)
                 link?  (= action :view-in-pe)]]
       ^{:key label}
       [:button
        {:class (if link?
                  "flex items-center gap-1.5 rounded-md border border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-100 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-900/50 transition-colors cursor-pointer"
                  "flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 hover:border-violet-400 hover:bg-violet-50 hover:text-violet-700 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-300 dark:hover:border-violet-500 dark:hover:text-violet-300 transition-colors cursor-pointer")
         :on-click (chip-handler action label created-transform send-message)}
        [:span {:class "mr-0.5"} icon]
        label])]))

(defn- esc
  "HTML-escape before interpolating into report HTML — chart labels, SQL and
  category values originate from the LLM/warehouse and are untrusted."
  [v]
  (when (some? v)
    (gstring/htmlEscape (str v))))

(defn- chart-narrative [{:keys [title chart_type x_axis y_axis row-count summary]}]
  (let [{:keys [total average max min pct-change trend first-cat last-cat]} summary
        pct-str (when (and pct-change (not (js/isNaN pct-change)))
                  (str (if (= trend :up) "↑" "↓")
                       " "
                       (.toFixed (js/Math.abs pct-change) 1)
                       "%"))]
    (str
     "<p>"
     "<strong>" (esc title) "</strong> presents " row-count " data points comparing "
     "<em>" (esc x_axis) "</em> against <em>" (esc y_axis) "</em>. "
     (when (and total (not (js/isNaN total)))
       (str "The combined total for <em>" (esc y_axis) "</em> is <strong>" (fmt-num total) "</strong>, "
            "averaging <strong>" (fmt-num average) "</strong> per data point. "))
     (when (and max min)
       (str "Values range from <strong>" (fmt-num min) "</strong> to <strong>" (fmt-num max) "</strong>. "))
     (when (and first-cat last-cat pct-str)
       (str "Between <em>" (esc first-cat) "</em> and <em>" (esc last-cat) "</em>, the metric "
            (if (= trend :up) "increased" "decreased")
            " by <strong>" pct-str "</strong>. "))
     (if (= trend :up)
       "The positive trajectory suggests continued momentum — consider scaling efforts in high-performing areas and monitoring for further acceleration opportunities."
       (if (= trend :down)
         "The declining trend warrants attention; reviewing contributing factors and implementing corrective actions is recommended."
         ""))
     "</p>")))

(defn- report-table-html [{:keys [header rows x-col val-cols]}]
  (str "<table class='data-table'><thead><tr>"
       (apply str (map #(str "<th>" (esc %) "</th>") header))
       "</tr></thead><tbody>"
       (apply str
              (map (fn [row]
                     (str "<tr>"
                          (apply str (map-indexed (fn [j v]
                                                    (str "<td" (when (and (or (number? v) (nil? v)) (not= j x-col)) " class='num'") ">"
                                                         (esc (cond
                                                                (and (number? v)
                                                                     (or (contains? val-cols j) (not (integer? v)))) (fmt-cell v)
                                                                (nil? v) "–"
                                                                :else    (str v)))
                                                         "</td>"))
                                                  row))
                          "</tr>"))
                   rows))
       "</tbody></table>"))

(defn- full-report-html [entries]
  (let [today  (.toLocaleDateString (js/Date.) js/undefined #js {:dateStyle "long"})
        n      (count entries)
        nonce  (.randomUUID js/crypto)
        fmt    (fn [v] (or (fmt-compact v) "–"))
        dt-row (fn [k v] (when v (str "<tr><td class='dt-k'>" k "</td><td class='dt-v'>" v "</td></tr>")))]
    (str
     "<!DOCTYPE html><html><head>"
     "<meta charset='UTF-8'>"
     "<meta http-equiv='Content-Security-Policy' content=\"default-src 'none'; script-src 'nonce-" nonce "'; style-src 'unsafe-inline'; img-src data:; base-uri 'none'; form-action 'none'\">"
     "<title>KAI Analytics Report</title>"
     "<style>"
     "body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;"
     "font-size:13px;padding:24px 40px;color:#1e293b;max-width:720px;margin:0 auto}"
     "h1{font-size:19px;font-weight:700;color:#1e1b4b;margin:0 0 3px}"
     "h2{font-size:13px;font-weight:700;color:#1e293b;margin:0 0 2px}"
     ".meta{color:#94a3b8;font-size:11px;margin-bottom:18px;"
     "border-bottom:1px solid #e2e8f0;padding-bottom:10px}"
     ".chart-meta{color:#94a3b8;font-size:11px;margin-bottom:8px}"
     ".svg-wrap{max-width:82%;overflow:hidden;margin:12px 0;line-height:0}"
     "svg{width:100%!important;height:auto!important;display:block}"
     ".data-table{width:100%;border-collapse:collapse;margin:12px 0;font-size:11px}"
     ".data-table th{text-align:left;font-size:9px;font-weight:600;color:#94a3b8;"
     "text-transform:uppercase;letter-spacing:.04em;padding:4px 6px;border-bottom:1px solid #e2e8f0}"
     ".data-table td{padding:3px 6px;border-bottom:1px solid #f1f5f9;color:#1e293b}"
     ".data-table td.num{text-align:right;font-family:monospace}"
     ".data-table tr{page-break-inside:avoid}"
     ".metric{margin:12px 0;padding:16px;text-align:center;background:#f8fafc;"
     "border:1px solid #e2e8f0;border-radius:8px}"
     ".metric-value{font-size:28px;font-weight:700;color:#1e1b4b}"
     ".metric-label{font-size:11px;color:#94a3b8;margin-top:2px}"
     ".exec{background:#f8f7ff;border:1px solid #ede9fe;border-radius:8px;"
     "padding:11px 14px;margin:8px 0 12px;page-break-inside:avoid}"
     ".exec-title{font-size:9px;font-weight:700;color:#6d28d9;"
     "text-transform:uppercase;letter-spacing:.08em;margin-bottom:6px}"
     ".exec-desc p{font-size:12px;color:#374151;line-height:1.6;margin:0}"
     ".kpi-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:5px;margin-top:9px}"
     ".kpi{background:#fff;border:1px solid #ede9fe;border-radius:6px;padding:6px 9px}"
     ".kpi-label{font-size:9px;font-weight:600;color:#7c3aed;"
     "text-transform:uppercase;letter-spacing:.04em;margin-bottom:2px}"
     ".kpi-value{font-size:12px;font-weight:700;color:#1e1b4b}"
     ".kpi-sub{font-size:9px;color:#94a3b8;margin-top:1px;line-height:1.3}"
     ".up{color:#059669}.down{color:#dc2626}"
     ".section{margin-top:10px;background:#f8fafc;border:1px solid #e2e8f0;"
     "border-radius:6px;padding:10px 12px;page-break-inside:avoid}"
     ".label{font-size:9px;font-weight:600;color:#94a3b8;"
     "text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px}"
     "pre{font-family:monospace;font-size:11px;color:#334155;white-space:pre-wrap;margin:0}"
     ".details-table{width:100%;border-collapse:collapse}"
     ".dt-k{font-size:10px;font-weight:600;color:#94a3b8;text-transform:uppercase;"
     "letter-spacing:.03em;padding:3px 8px 3px 0;width:70px;vertical-align:top}"
     ".dt-v{font-size:11px;color:#1e293b;padding:3px 0;font-family:monospace}"
     ".type-pill{display:inline-block;background:#ede9fe;color:#6d28d9;"
     "border:1px solid #ddd6fe;border-radius:8px;padding:1px 6px;"
     "font-size:10px;font-weight:600}"
     ".chart-block{page-break-inside:avoid;margin-bottom:26px;"
     "border-bottom:1px solid #e2e8f0;padding-bottom:20px}"
     ".table-block{page-break-inside:auto}"
     ".footer{margin-top:20px;font-size:10px;color:#94a3b8;"
     "border-top:1px solid #e2e8f0;padding-top:8px;"
     "display:flex;justify-content:space-between}"
     ".print-btn{display:inline-flex;align-items:center;gap:6px;"
     "margin-bottom:18px;padding:7px 16px;background:#7c3aed;color:#fff;"
     "border:none;border-radius:6px;cursor:pointer;font-size:12px;font-weight:600}"
     "@media print{.print-btn{display:none}}"
     "</style>"
     "<script nonce='" nonce "'>"
     "window.addEventListener('load',function(){"
     "var pb=document.getElementById('kai-print-btn');"
     "if(pb)pb.addEventListener('click',function(){window.print();});"
     "var mo=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];"
     "function fc(v){var n=parseFloat(String(v).replace(/,/g,''));if(isNaN(n))return null;"
     "if(n===Math.floor(n)&&n>=1900&&n<=2100)return null;"
     "var a=Math.abs(n);"
     "if(a>=1e9)return(n/1e9).toFixed(1).replace(/\\.0$/,'')+' B';"
     "if(a>=1e6)return(n/1e6).toFixed(1).replace(/\\.0$/,'')+' M';"
     "if(a>=1e3)return(n/1e3).toFixed(1).replace(/\\.0$/,'')+' K';"
     "return String(v).replace(/\\.0+$/,'');}"
     "function fd(t){var m=t.match(/^(\\d{4})(\\d{2})(\\d{2})$/);"
     "if(!m)return null;"
     "var d=new Date(m[1]+'-'+m[2]+'-'+m[3]);"
     "if(isNaN(d.getTime()))return null;"
     "return mo[d.getMonth()]+' '+m[1];}"
     "document.querySelectorAll('svg').forEach(function(svg){"
     "svg.querySelectorAll('.apexcharts-yaxis text,.apexcharts-yaxis-label').forEach(function(el){"
     "var t=el.textContent.trim();"
     "if(/^-?[\\d,]+(\\.\\d+)?$/.test(t)){var r=fc(t);if(r)el.textContent=r;}});"
     "svg.querySelectorAll('.apexcharts-xaxis text,.apexcharts-xaxis-label').forEach(function(el){"
     "var t=el.textContent.trim();"
     "if(/^\\d{8}$/.test(t)){var r=fd(t);if(r)el.textContent=r;}});"
     "});});"
     "</script>"
     "</head><body>"
     "<button class='print-btn' id='kai-print-btn'>⬇ Save as PDF</button>"
     "<h1>Report</h1>"
     "<div class='meta'>Generated by Kleene AI. " today
     " · " n " chart" (when (> n 1) "s") "</div>"
     (apply str
            (map-indexed
             (fn [i {:keys [title chart_type x_axis y_axis color_by sql row-count summary svg-html
                            table metric-value]}]
               (let [{:keys [total average max min pct-change trend first-cat last-cat]} summary
                     pct-str (when (and pct-change (not (js/isNaN pct-change)))
                               (str (if (= trend :up) "↑" "↓")
                                    " "
                                    (.toFixed (js/Math.abs pct-change) 1)
                                    "%"))
                     metric? (= chart_type "metric")]
                 (str
                  "<div class='chart-block" (when table " table-block") "'>"
                  "<h2>" (inc i) ". " (esc title) "</h2>"
                  "<div class='chart-meta'>"
                  (esc chart_type) " · "
                  (when (seq x_axis) (str (esc x_axis) " vs "))
                  (esc y_axis) " · " row-count " rows</div>"
                  (when-not (or metric? table)
                    (str
                     "<div class='exec'>"
                     "<div class='exec-title'>Executive Summary</div>"
                     "<div class='exec-desc'>"
                     (chart-narrative {:title title
                                       :chart_type chart_type
                                       :x_axis x_axis
                                       :y_axis y_axis
                                       :row-count row-count
                                       :summary summary})
                     "</div>"
                     (when summary
                       (str
                        "<div class='kpi-grid'>"
                        "<div class='kpi'><div class='kpi-label'>Total</div>"
                        "<div class='kpi-value'>" (fmt total) "</div></div>"
                        "<div class='kpi'><div class='kpi-label'>Average</div>"
                        "<div class='kpi-value'>" (fmt average) "</div></div>"
                        "<div class='kpi'><div class='kpi-label'>Max</div>"
                        "<div class='kpi-value'>" (fmt max) "</div></div>"
                        "<div class='kpi'><div class='kpi-label'>Min</div>"
                        "<div class='kpi-value'>" (fmt min) "</div></div>"
                        "<div class='kpi'><div class='kpi-label'>Period Change</div>"
                        "<div class='kpi-value'>"
                        (if pct-str
                          (str "<span class='" (if (= trend :up) "up" "down") "'>" pct-str "</span>")
                          "–")
                        "</div>"
                        (when (and first-cat last-cat)
                          (str "<div class='kpi-sub'>" (esc first-cat) " → " (esc last-cat) "</div>"))
                        "</div></div>"))
                     "</div>"))
                  (cond
                    metric? (str "<div class='metric'><div class='metric-value'>"
                                 (or (fmt-num metric-value) "–")
                                 "</div><div class='metric-label'>" (esc y_axis) "</div></div>")
                    table   (report-table-html table)
                    :else   (str "<div class='svg-wrap'>" (or svg-html "") "</div>"))
                  (when sql
                    (str "<div class='section'><div class='label'>SQL Query</div>"
                         "<pre>" (esc sql) "</pre></div>"))
                  "<div class='section'><div class='label'>Chart Details</div>"
                  "<table class='details-table'>"
                  (dt-row "Title" (esc title))
                  (dt-row "Type" (str "<span class='type-pill'>" (esc chart_type) "</span>"))
                  (dt-row "X-Axis" (esc x_axis))
                  (dt-row "Y-Axis" (esc y_axis))
                  (when color_by (dt-row "Color By" (esc color_by)))
                  (dt-row "Rows" (str row-count))
                  "</table></div>"
                  "</div>")))
             entries))
     "<div class='footer'><span>Generated by Kleene AI. Please verify all information independently, as AI may occasionally produce inaccurate or incomplete responses.</span><span>" today "</span></div>"
     "</body></html>")))

(defn- svg-for-report [s]
  ;; Clone so we don't mutate the live DOM
  (let [clone (.cloneNode s true)
        sw    (or (.getAttribute s "width") "420")
        sh    (or (.getAttribute s "height") "280")]
    ;; Add viewBox so width:100% scales content correctly
    (when-not (.getAttribute clone "viewBox")
      (.setAttribute clone "viewBox" (str "0 0 " sw " " sh)))
    (.setAttribute clone "width" "100%")
    (.removeAttribute clone "height")
    (.-outerHTML clone)))

(defn- download-report! []
  (let [all-charts   @kai-charts
        latest-batch (apply max 0 (map :batch all-charts))
        batch-charts (filterv #(= (:batch %) latest-batch) all-charts)
        entries      (mapv (fn [{:keys [id] :as entry}]
                             (if-let [c (js/document.getElementById id)]
                               (if-let [s (.querySelector c "svg")]
                                 (assoc entry :svg-html (svg-for-report s))
                                 entry)
                               entry))
                           batch-charts)
        w            (.open js/window "" "_blank")]
    (.write (.-document w) (full-report-html entries))
    (.close (.-document w))))

(defn chart-message [{:keys [chart]}]
  (let [{:keys [chart_type title x_axis y_axis color_by columns rows sql]} chart
        chart-id    (str "kai-chart-" (hash (str title sql chart_type)))
        col-idx     (fn [col-name]
                      (first (keep-indexed
                              #(when (= (clojure.string/lower-case (str col-name))
                                        (clojure.string/lower-case (str %2))) %1)
                              columns)))
        x-idx       (or (col-idx x_axis) 0)
        y-idx       (or (col-idx y_axis) 1)
        color-idx   (when color_by (col-idx color_by))
        multi-series? (some? color-idx)
        all-rows    rows
        rows        (filterv #(some? (nth % x-idx nil)) rows)
        raw-x-vals  (mapv #(let [s (str (nth % x-idx ""))
                                 t (clojure.string/index-of s "T")]
                             (if (and t (pos? t)) (subs s 0 t) s))
                          rows)
        unique-raw-x (if multi-series?
                       (vec (distinct raw-x-vals))
                       raw-x-vals)
        cats        (fmt-date-series unique-raw-x)
        parse-y     (fn [row] (let [v (nth row y-idx 0)]
                                (if (number? v) v (js/parseFloat v))))
        data        (mapv parse-y rows)
        groups      (when multi-series?
                      (vec (distinct (mapv #(str (nth % color-idx "")) rows))))
        series-data (if multi-series?
                      (mapv (fn [grp]
                              (let [grp-map (into {}
                                                  (mapv #(vector (let [s (str (nth % x-idx ""))
                                                                       t (clojure.string/index-of s "T")]
                                                                   (if (and t (pos? t)) (subs s 0 t) s))
                                                                 (parse-y %))
                                                        (filterv #(= (str (nth % color-idx "")) grp) rows)))]
                                {:name grp
                                 :data (mapv #(get grp-map % nil) unique-raw-x)}))
                            groups)
                      [{:name y_axis :data data}])
        pie?               (= chart_type "pie")
        line-area?         (contains? #{"line" "area"} chart_type)
        bar?               (= chart_type "bar")
        table?             (= chart_type "table")
        metric?            (= chart_type "metric")
        iso-day            (fn [v] (or (second (re-find #"^(\d{4}-\d{2}-\d{2})T00:00(?::00(?:\.0+)?)?Z?$" (str v)))
                                       v))
        table-groups       (when multi-series? (vec (distinct (map #(str (nth % color-idx "")) all-rows))))
        table-header       (if multi-series? (into [(if (seq x_axis) x_axis (nth columns x-idx ""))] table-groups) columns)
        table-rows         (if multi-series?
                             (let [cells (into {} (map (fn [r] [[(iso-day (nth r x-idx "")) (str (nth r color-idx ""))]
                                                                (let [v (parse-y r)] (when-not (js/isNaN v) v))])
                                                       all-rows))]
                               (mapv (fn [x] (into [x] (map #(get cells [x %]) table-groups)))
                                     (distinct (map #(iso-day (nth % x-idx "")) all-rows))))
                             (mapv #(if (< x-idx (count %)) (update % x-idx iso-day) %) all-rows))
        metric-idx         (or (col-idx y_axis) (dec (count columns)))
        metric-val         (let [v (nth (first all-rows) metric-idx nil)]
                             (if (number? v) v (js/parseFloat v)))
        dark-mode?         (boolean (or (-> js/document .-documentElement .-classList (.contains "dark"))
                                        (-> js/document .-body .-classList (.contains "dark"))))
        colors10           ["#64FCAB" "#4f46e5" "#0ea5e9" "#f59e0b" "#ef4444"
                            "#8b5cf6" "#06b6d4" "#84cc16" "#f97316" "#0C0330"]
        single-series-bar? (and bar? (not pie?) (not multi-series?))
        bar-layout         (when single-series-bar? (choose-bar-layout cats))
        horizontal-bar?    (= bar-layout :horizontal)
        rotated-x-labels?  (= bar-layout :vertical-rotated)
        truncate-x-labels? (not horizontal-bar?)
        series-color       "#64FCAB"
        label-color        (if dark-mode? "#94a3b8" "#475569")
        title-color        (if dark-mode? "#e2e8f0" "#0f172a")
        grid-color         (if dark-mode? "#334155" "#e2e8f0")
        axis-color         (if dark-mode? "#475569" "#cbd5e1")
        legend-color       (if dark-mode? "#cbd5e1" "#475569")
        opts               (clj->js
                            (if pie?
                              {:labels  cats
                               :title   {:text title :align "left" :style {:color title-color}}
                               :theme   {:mode (if dark-mode? "dark" "light")}
                               :chart   {:background "transparent"
                                         :foreColor  label-color
                                         :toolbar    {:show true :tools {:download true}}}
                               :legend  {:position "bottom"
                                         :labels   {:colors legend-color}}
                               :tooltip {:theme (if dark-mode? "dark" "light") :style {:fontSize "12px"}}
                               :colors  colors10}
                              (cond->
                               {:theme      {:mode (if dark-mode? "dark" "light")}
                                :chart      {:type       chart_type
                                             :background "transparent"
                                             :foreColor  label-color
                                             :toolbar    {:show  true
                                                          :tools {:download  true
                                                                  :selection false
                                                                  :zoom      false
                                                                  :zoomin    false
                                                                  :zoomout   false
                                                                  :pan       false
                                                                  :reset     false}}}
                                :title      {:text title :align "left" :style {:color title-color}}
                                :grid       {:borderColor grid-color}
                                :dataLabels {:enabled false}
                                :tooltip    {:theme (if dark-mode? "dark" "light") :style {:fontSize "12px"}}
                                :colors     (if single-series-bar?
                                              [series-color]
                                              colors10)
                                :legend     {:show   (not single-series-bar?)
                                             :labels {:colors legend-color}}

                                :xaxis (if horizontal-bar?
                                         {:categories cats
                                          :labels     {:formatter (fn [val] (or (fmt-compact val) (str val)))
                                                       :style     {:colors   [label-color]
                                                                   :fontSize "11px"}}
                                          :axisBorder {:color axis-color}
                                          :axisTicks  {:color axis-color}}
                                         {:type       "category"
                                          :categories cats
                                          :axisBorder {:color axis-color}
                                          :axisTicks  {:color axis-color}
                                          :crosshairs {:stroke {:color axis-color}}
                                          :labels     {:show                  true
                                                       :rotate                (if rotated-x-labels? -45 0)
                                                       :hideOverlappingLabels true
                                                       :rotateAlways          rotated-x-labels?
                                                       :showDuplicates        false
                                                       :trim                  false
                                                       :formatter             (fn [val]
                                                                                (if truncate-x-labels?
                                                                                  (ellipsize (str val) 12)
                                                                                  (str val)))
                                                       :style                 {:colors   (repeated-colors (count cats) label-color)
                                                                               :fontSize "11px"}}})

                                :yaxis (if horizontal-bar?
                                         {:labels {:show  true
                                                   :style {:colors   [label-color]
                                                           :fontSize "11px"}}}
                                         {:labels {:formatter (fn [val] (or (fmt-compact val) (str val)))
                                                   :style     {:colors   [label-color]
                                                               :fontSize "11px"}}})}

                                line-area? (assoc :stroke {:curve "smooth" :width 2})

                                single-series-bar?
                                (assoc :plotOptions
                                       {:bar {:horizontal  horizontal-bar?
                                              :distributed false
                                              :borderRadius 4
                                              :columnWidth  (if rotated-x-labels? "55%" "42%")
                                              :barHeight    "60%"}}))))
        series      (clj->js (if pie? data series-data))
        row-count   (if table? (count table-rows) (count (if metric? all-rows rows)))
        sum-total   (reduce + 0 data)
        first-val   (first data)
        last-val    (last data)
        first-cat   (str (first cats))
        last-cat    (str (last cats))
        pct-change  (when (and first-val last-val
                               (not (js/isNaN first-val))
                               (not (js/isNaN last-val))
                               (not (zero? first-val)))
                      (* 100.0 (/ (- last-val first-val) first-val)))
        trend       (when pct-change (if (>= pct-change 0) :up :down))
        summary     (when (seq data)
                      {:total      sum-total
                       :average    (/ sum-total (count data))
                       :max        (reduce max data)
                       :min        (reduce min data)
                       :first-val  first-val
                       :last-val   last-val
                       :first-cat  first-cat
                       :last-cat   last-cat
                       :pct-change pct-change
                       :trend      trend})
        my-batch    @kai-batch
        _           (swap! kai-charts
                           (fn [cs]
                             (if (some #(= (:id %) chart-id) cs)
                               cs
                               (conj cs (cond-> {:id         chart-id
                                                 :batch      my-batch
                                                 :title      title
                                                 :chart_type chart_type
                                                 :x_axis     x_axis
                                                 :y_axis     y_axis
                                                 :color_by   color_by
                                                 :sql        sql
                                                 :row-count  row-count
                                                 :summary    summary}
                                          table?  (assoc :table {:header   table-header
                                                                 :rows     table-rows
                                                                 :x-col    (if multi-series? 0 x-idx)
                                                                 :val-cols (if multi-series? (set (range 1 (count table-header))) #{y-idx})})
                                          metric? (assoc :metric-value metric-val))))))
        active-tab  (reagent/atom "preview")
        copied      (reagent/atom nil)
        copy!       (fn [text label]
                      (.writeText (.-clipboard js/navigator) text)
                      (reset! copied label)
                      (js/setTimeout #(reset! copied nil) 2000))
        dl-pdf      (fn []
                      (let [batch-charts (filterv #(= (:batch %) my-batch) @kai-charts)
                            entries      (mapv (fn [{:keys [id] :as entry}]
                                                 (if-let [c (js/document.getElementById id)]
                                                   (if-let [s (.querySelector c "svg")]
                                                     (assoc entry :svg-html (svg-for-report s))
                                                     entry)
                                                   entry))
                                               batch-charts)
                            w            (.open js/window "" "_blank")]
                        (.write (.-document w) (full-report-html entries))
                        (.close (.-document w))))]
    (fn []
      [:div {:class "space-y-4 w-full relative max-w-[42rem] pt-3 pb-3 px-2"}
       [:div {:class "flex gap-3"}
        [:div {:class "kai-chart-card w-full rounded-xl border border-[#64FCAB]/60 bg-white overflow-hidden dark:bg-slate-900 dark:border-slate-700"}
         (when dark-mode?
           [:style (str "
/* axis / legend text inside this chart */
#" chart-id " text { fill:#e2e8f0!important }
#" chart-id " .apexcharts-legend-text { color:#e2e8f0!important }
#" chart-id " .apexcharts-gridline { stroke:#334155!important }
#" chart-id " .apexcharts-xaxis line,
#" chart-id " .apexcharts-yaxis line { stroke:#475569!important }
#" chart-id " .apexcharts-toolbar svg { fill:#94a3b8!important }
/* main tooltip — dark bg, light text */
.apexcharts-tooltip { background:#1e293b!important; border-color:#334155!important }
.apexcharts-tooltip * { color:#e2e8f0!important }
.apexcharts-tooltip-title { color:#f1f5f9!important; background:#0f172a!important; border-bottom:1px solid #334155!important }
.apexcharts-tooltip-text-y-label { color:#cbd5e1!important }
.apexcharts-tooltip-text-y-value { color:#f1f5f9!important; font-weight:600!important }
/* x-axis crosshair tooltip — dark bg, light text */
.apexcharts-xaxistooltip { background:#1e293b!important; border-color:#475569!important }
.apexcharts-xaxistooltip-text { color:#f1f5f9!important }
.apexcharts-xaxistooltip:before,
.apexcharts-xaxistooltip:after { border-bottom-color:#1e293b!important }
")])
         ;; card header
         [:div {:class "bg-[#f0fff8] px-3 py-2 border-b border-[#64FCAB]/30 flex items-center gap-2 dark:bg-slate-800 dark:border-slate-700"}
          [:> BarChart2 {:size 13 :class "text-[#0C0330] dark:text-[#64FCAB]"}]
          [:span {:class "text-xs font-bold text-[#0C0330] flex-1 dark:text-slate-100"} title]
          [:span {:class "text-[10px] bg-[#e6fff4] text-[#047857] border border-[#64FCAB]/50 px-2 py-0.5 rounded-full shrink-0 dark:bg-[#064e3b] dark:text-[#64FCAB] dark:border-[#64FCAB]/40"} chart_type]]
         ;; tab bar
         [:div {:class "flex border-b border-[#64FCAB]/20 dark:border-slate-700"}
          (for [[id label] [["preview" (cond table? "Table" metric? "Metric" :else "Chart Preview")]
                            ["sql" "SQL"] ["details" "Details"]]]
            ^{:key id}
            [:button
             {:class (str "px-3.5 py-2 text-xs cursor-pointer border-b-2 transition-colors "
                          (if (= @active-tab id)
                            "text-[#0C0330] border-[#64FCAB] font-semibold dark:text-[#64FCAB]"
                            "text-slate-500 border-transparent hover:text-slate-700 dark:hover:text-slate-300"))
              :on-click #(reset! active-tab id)}
             label])]
         ;; chart tab — always in DOM so SVG is available for PDF export
         [:div {:id    chart-id
                :style {:display (if (= @active-tab "preview") "block" "none")}
                :class "p-3 dark:bg-slate-900"}
          (cond
            table?
            [:f> data-grid-preview/table
             {:headers      table-header
              :rows         table-rows
              :dark-mode?   dark-mode?
              :height       (str (max 100 (min (+ 3 (* 25 (inc (count table-rows)))) 300)) "px")
              :column-width 150
              :sortable?    true
              :filterable?  (> (count table-rows) 7)
              :resizable?   true
              :row-numbers? true
              :header-tooltips? true}]

            metric?
            [:div {:class "py-6 text-center"}
             [:div {:class "text-4xl font-bold text-[#0C0330] dark:text-slate-100"}
              (or (fmt-num metric-val) "–")]
             [:div {:class "mt-1 text-xs text-slate-400"} y_axis]]

            :else
            [:> ApexChart {:key     (str chart-id "-" (if dark-mode? "dark" "light"))
                           :type    (if pie? "pie" chart_type)
                           :height  (if horizontal-bar?
                                      (max 280 (+ 70 (* row-count 28)))
                                      280)
                           :width   "100%"
                           :options opts
                           :series  series}])]
         ;; sql tab
         [:div {:style {:display (if (= @active-tab "sql") "block" "none")}}
          [:div {:class "flex justify-end px-3 pt-2 bg-slate-800"}
           [:button
            {:class "flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
             :on-click #(copy! (or sql "") "sql")}
            (if (= @copied "sql")
              [:<> [:> Check {:size 12}] " Copied!"]
              [:<> [:> ClipboardCopy {:size 12}] " Copy"])]]
          [:pre {:class "text-xs font-mono px-3 pb-3 text-slate-300 bg-slate-800 overflow-auto max-h-52 m-0 leading-relaxed"} sql]]
         ;; details tab
         [:div {:style {:display (if (= @active-tab "details") "block" "none")}
                :class "p-3"}
          (let [detail-rows (cond-> [["Title" title]
                                     ["Type" chart_type]
                                     ["X-Axis" x_axis]
                                     ["Y-Axis" y_axis]
                                     ["Rows" (str row-count)]]
                              color_by (conj ["Color By" color_by]))]
            [:table {:class "w-full text-xs border-collapse"}
             [:tbody
              (for [[k v] detail-rows]
                ^{:key k}
                [:tr {:class "border-b border-slate-100 last:border-0 dark:border-slate-800"}
                 [:td {:class "py-2 pr-3 text-slate-400 uppercase tracking-wider font-semibold w-20 align-top"} k]
                 [:td {:class "py-2 text-slate-700 font-mono dark:text-slate-200"} v]])]])]
         ;; footer
         [:div {:class "flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50 dark:bg-slate-800 dark:border-slate-700"}
          [:span {:class "text-xs text-slate-400"} (str row-count " rows")]
          [:button
           {:class "flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 hover:bg-[#f0fff8] hover:border-[#64FCAB] hover:text-[#047857] dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:border-[#64FCAB]"
            :on-click dl-pdf
            :title "Download full report as PDF"}
           [:> FilePlus2 {:size 13}]
           " Download Full Report"]]]]])))

(defn tool-call-message [{:keys [name args partial id]}]
  (when-not partial
    (let [readable-tool-name (get tools name)]
      [:div {:class "space-y-4 w-full relative max-w-[42rem] pt-3 pb-3 px-2"}
       [:div
        {:class "flex gap-3"}
        [:span
         {:class
          "shrink-0 inline-flex size-10 items-center justify-center rounded-full bg-gray-500 outline -outline-offset-1 outline-black/5 dark:bg-gray-800 dark:outline-white/10"}
         [:span {:class "font-medium text-white"}
          [:> ServerCog {:size 20}]]]

        [:div
         {:class ["rounded-lg pt-2.5 pb-0 px-4 text-foreground bg-secondary prose text-sm break-words whitespace-normal dark:prose-invert"
                  " bg-gray-500 outline -outline-offset-1 outline-black/5 dark:bg-gray-800 dark:outline-white/10"
                  "overflow-auto text-white"]}
         (if readable-tool-name
           [:p readable-tool-name "..."]
           [:p "Calling tool: " [:code name]])
         #_[:pre {:class "text-xs"} (str args)]]]])))

(defn tool-call-result-message [{:keys [response]}]
  #_[:div {:class "space-y-4 w-full relative max-w-[42rem] pt-3 pb-3 px-2"}
     [:div
      {:class "flex gap-3"}
      [:span
       {:class
        "shrink-0 inline-flex size-10 items-center justify-center rounded-full bg-gray-500 outline -outline-offset-1 outline-black/5 dark:bg-gray-800 dark:outline-white/10"}
       [:span {:class "font-medium text-white"}
        [:> MessageCircleCode {:size 20}]]]

      [:div
       {:class ["rounded-lg p-2 text-foreground bg-secondary prose text-sm break-words whitespace-normal dark:prose-invert"
                " bg-gray-500 outline -outline-offset-1 outline-black/5 dark:bg-gray-800 dark:outline-white/10"]}
       [:pre {:class "text-xs whitespace-pre-wrap max-h-[300px]"} (str (or (:result response) response))]]]])

(defn- proposal-markdown
  [{:keys [action old-sql sql]}]
  (if (= action "create")
    (str "```sql\n" sql "\n```")
    (str "```diff\n"
         (clojure.string/join
          "\n"
          (concat (map #(str "- " %) (clojure.string/split-lines (or old-sql "")))
                  (map #(str "+ " %) (clojure.string/split-lines (or sql "")))))
         "\n```")))

(defn- canonical-approval-message
  [{:keys [action name group-id master-id sql]}]
  (let [edit? (= action "edit")
        lines (cond-> ["Approved. Apply the proposal verbatim:"
                       (str "ACTION=" action)]
                (seq name)                       (conj (str "NAME=" name))
                (and (not edit?) (seq group-id)) (conj (str "GROUP_ID=" group-id))
                (and edit? (seq master-id))      (conj (str "MASTER_ID=" master-id))
                true                             (conj "SQL=" sql))]
    (clojure.string/join "\n" lines)))

(defn transform-proposal-message [{:keys [proposal send-message]}]
  (let [local-status (reagent/atom :pending)
        dialog-open? (reagent/atom false)
        on-confirm   (fn []
                       (reset! dialog-open? false)
                       (reset! local-status :approved)
                       (send-message (canonical-approval-message proposal)))
        on-insert    (fn [{:keys [action sql]}]
                       (reset! local-status :inserted)
                       (if (= action "create")
                         (dispatch [:sql-console-modal.events/open-new-tab-with-code sql])
                         (dispatch [:sql-console-modal.events/set-editor-content sql])))]
    (fn [{:keys [proposal]}]
      (let [create?       (= (:action proposal) "create")
            applied?      (some? (:created-transform proposal))
            superseded?   (= :superseded (:status proposal))
            sql-console?  @(re-frame/subscribe [:sql-console-modal.subs/modal-open?])
            existing      (when-let [mid (:master-id proposal)]
                            @(re-posh/subscribe [:pipeline-editor.subs/full-transform mid]))
            display-name  (or (:name proposal) (:name existing) "transform")
            group-id      (or (:group-id proposal) (:group-id existing))
            group-name    (when group-id
                            (:group/name @(re-posh/subscribe [:group/pull [:group/id group-id]])))
            status        (cond applied?    :applied
                                superseded? :superseded
                                :else       @local-status)]
        [:div {:class "space-y-3 w-full relative max-w-[42rem] pt-3 pb-3 px-2"}
         [:div {:class "flex gap-3"}
          [:div {:class
                 "rounded-lg p-2 text-foreground bg-secondary prose text-sm break-words whitespace-normal prose-h2:mt-0! prose-h2:scroll-m-0! dark:prose-invert overflow-auto w-full"}
           [formatted-text {:text (proposal-markdown proposal)}]
           (case status
             :applied    [:p {:class "text-xs text-kleene-text-success mt-2 not-prose"}
                          (if create? "✓ Created in Sandbox." "✓ Updated in Sandbox.")]
             :inserted   [:p {:class "text-xs text-kleene-text-success mt-2 not-prose"}
                          (if create?
                            "✓ Inserted into a new SQL Console tab — review and Save to keep it."
                            "✓ Inserted into the open SQL Console tab — review and Save to keep it.")]
             :approved   [:p {:class "text-xs text-kleene-text-secondary mt-2 not-prose"} "Sent to KAI. Applying in Sandbox…"]
             :cancelled  [:p {:class "text-xs text-kleene-text-secondary mt-2 not-prose"} "Proposal cancelled."]
             :superseded [:p {:class "text-xs text-kleene-text-secondary mt-2 not-prose italic"} "Replaced by a newer proposal."]
             nil)]]
         (when (= status :pending)
           [:div {:class "sticky bottom-0 z-10 -mx-2 px-3 py-2 border-t border-kleene-input-border bg-kleene-background-secondary rounded-b-lg flex flex-wrap items-center gap-2"}
            [:div {:class "flex flex-wrap items-center gap-1.5 text-xs min-w-0 flex-1 basis-full sm:basis-auto"}
             [:span {:class "font-medium"}
              (if create? "⚡ KAI wants to create:" "⚡ KAI wants to edit:")]
             [:code {:class "px-1.5 py-0.5 rounded bg-kleene-background break-all"} display-name]
             [:span {:class "text-kleene-text-secondary"}
              (if create? "→ will land in" "→ saved to")]
             [ui.chip/chip {:variant :main} "Sandbox"]]
            [:div {:class "flex flex-wrap gap-2 ml-auto justify-end"}
             [button {:variant :secondary :size :small
                      :on-click #(reset! local-status :cancelled)} "Cancel"]
             (when sql-console?
               [button {:variant :secondary :size :small
                        :on-click #(on-insert proposal)} "✓ Allow Insert"])
             [button {:variant :primary :size :small
                      :on-click #(reset! dialog-open? true)}
              (if create? "✓ Create Transform in Sandbox" "✓ Update in Sandbox")]]])
         [dialog-root {:open @dialog-open? :on-open-change #(reset! dialog-open? %)}
          [dialog-content {:size :small :class "z-[19000]"}
           [dialog-title (if create? "Create transform in Sandbox?" "Update transform in Sandbox?")]
           [:p {:class "text-sm mt-2 text-kleene-text-secondary"}
            (if create?
              "KAI will create a new transform in the Pipeline Editor Sandbox (sandbox-only until reviewed and committed to Live)."
              "KAI will update this transform in the Pipeline Editor Sandbox (sandbox-only until reviewed and committed to Live).")]
           [:div {:class "flex items-start gap-2 mt-3 px-3 py-2 rounded bg-kleene-background-secondary border border-kleene-input-border"}
            [:code {:class "text-sm flex-1 min-w-0 break-all"} display-name]
            (when group-name
              [:span {:class "text-xs text-kleene-text-secondary shrink-0"} "in " [:strong group-name]])
            [ui.chip/chip {:variant :main} "Sandbox"]]
           [dialog-actions
            [button {:variant :secondary :size :small
                     :on-click #(reset! dialog-open? false)} "Cancel"]
            [button {:variant :primary :size :small
                     :on-click on-confirm}
             (if create? "Create in Sandbox" "Update in Sandbox")]]]]]))))

(defn confirmation-message [_]
  (let [clicked? (reagent/atom false)]
    (fn [{:keys [message on-answer loading?]}]
      (let [{:keys [id action preview]} message
            label  (confirmation/action-label action)
            status (confirmation/status-text (:answer message))
            answer (fn [confirmed?]
                     (when-not @clicked?
                       (reset! clicked? true)
                       (on-answer id confirmed?)))]
        [:div {:class "space-y-3 w-full relative max-w-[42rem] pt-3 pb-3 px-2"}
         [:div {:class "flex gap-3"}
          [:div {:class "rounded-lg p-2 text-foreground bg-secondary text-sm break-words whitespace-normal overflow-auto w-full"}
           [:p {:class "font-medium"} "⚡ KAI wants to: " label]
           (when-not (clojure.string/blank? (str preview))
             [:p {:class "mt-1"} (str preview)])
           [:p {:class (if status "text-xs text-kleene-text-secondary mt-2" "sr-only") :role "status"} status]]]
         (when (confirmation/pending? message)
           [:div {:class "sticky bottom-0 z-10 -mx-2 px-3 py-2 border-t border-kleene-input-border bg-kleene-background-secondary rounded-b-lg flex flex-wrap items-center gap-2 justify-end"}
            [button {:variant :secondary :size :small :disabled loading?
                     :on-click #(answer false)} "Cancel"]
            [button {:variant :primary :size :small :disabled loading?
                     :on-click #(answer true)} "Confirm"]])]))))

(defn render-message [{:keys [type author text partial] :as message} send-message answer-confirmation loading?]
  (if (= "user" author)
    ;; Users only ever send text messages
    [user-message {:text text}]

    ;; Assistant messages can be text, function call and function result
    (case type
      "text"                [manager-message {:text text :partial partial}]
      "function_call"       [tool-call-message (select-keys message [:name :args :id :partial])]
      "function_response"   [tool-call-result-message (select-keys message [:response :id :name :partial])]
      "chart"               [chart-message (select-keys message [:chart])]
      "transform_proposal"  [transform-proposal-message {:proposal (:proposal message)
                                                         :send-message send-message}]
      "confirmation_request" (when (confirmation/renderable? message)
                               ^{:key (:id message)}
                               [confirmation-message {:message   message
                                                      :on-answer answer-confirmation
                                                      :loading?  loading?}])
      nil)))

(defn render-messages [{:keys [send-message messages context sql-ctx loading? abort-request open?
                               prompt set-prompt answer-confirmation]}]
  (let [viewport (react/useRef nil)

        clear-chat (fn []
                     (reset! kai-charts [])
                     (reset! kai-batch 0)
                     (dispatch [:ai-chat/clear-server-chat])
                     (dispatch [:ai-chat/clear-chat]))

        send-message (fn [msg]
                       (swap! kai-batch inc)
                       (send-message msg))

        scroll-to-the-bottom (fn scroll-chat-window
                               ([] (scroll-chat-window "smooth"))
                               ([behavior]
                                (when viewport
                                  (let [el (.-current viewport)]
                                    (when el
                                      (.scroll el #js {:top (jsi/get el :scrollHeight) :behavior behavior}))))))]

    (react/useEffect
     (fn []
       (when (and open? (empty? messages))
         (clear-chat))
       (fn []))
     #js [messages open?])

    (react/useEffect
     (fn []
       ;; When new messages appear, only scroll to the bottom if the user is
       ;; already close to the bottom. If they scrolled away, keep the scroll
       ;; position as is.
       (when viewport
         (let [el (.-current viewport)]
           (when el
             (let [scroll-height (jsi/get el :scrollHeight)
                   scroll-top (jsi/get el :scrollTop)
                   offset-height (jsi/get el :offsetHeight)]
               ;; User is at the bottom when
               ;; scroll-top + offset-height == scroll-height
               ;; (more or less, there can be weird 0.5 pixel differences due
               ;; to scaled rendering)
               ;;
               ;; We will keep scrolling to the bottom only if within
               ;; 300px of the bottom
               (when (or (> 300 (- scroll-height scroll-top offset-height))
                         (= 0 scroll-top))
                 ;; auto smooth behavior for instant scrolling
                 (scroll-to-the-bottom "auto"))))))

       ;; return unmount callback
       (fn []))
     #js [messages])

    [:aside {:class (clsx " transition-all duration-300 print:hidden z-10 text-kleene-text h-full overflow-hidden relative")}

     [:div {:class ["absolute inset-0 z-0 background-squares opacity-50"]}]

     [:div {:class ["chat"
                    "flex h-full min-h-0 flex-col z-1 relative"]}

      [:div {:class "flex-1 min-h-0 overflow-y-scroll flex flex-col items-center" :ref viewport}

       (when-not (empty? messages)
         [:div {:class "h-full pt-5"}])

       (when (empty? messages)
         [:div {:class "h-full flex flex-col items-center justify-center px-4"}
          (if sql-ctx
            [:<>
             [:h3 {:class "text-base font-semibold text-slate-800 dark:text-slate-100 text-center mb-1 not-prose"}
              "Hi! I can see your " (:source sql-ctx) " is open."]
             [:p {:class "text-xs text-slate-400 dark:text-slate-500 text-center mb-5 not-prose"}
              "Ask me to create or edit transforms, explain SQL, or explore your data."]]
            [:<>
             [:h3 {:class "text-base font-semibold text-slate-800 dark:text-slate-100 text-center mb-1 not-prose"}
              "Hello, I'm " [:span {:class "text-kleene-primary-v2 dark:text-kleene-primary-01"} "K"] "ai — the Kleene AI agent."]
             [:p {:class "text-xs text-slate-400 dark:text-slate-500 text-center mb-5 not-prose"} "Ask me anything or pick a suggestion below"]])
          [:div {:class "grid grid-cols-2 gap-3 w-full max-w-sm"}
           (for [[icon title subtitle msg] [[[:> LayoutList {:size 20 :class "text-kleene-primary-v2 dark:text-kleene-primary-01"}] "Transform failures" "Last 24 hours" "Show transform failures in the last 24 hours"]
                                            [[:> Unplug {:size 20 :class "text-kleene-primary-v2 dark:text-kleene-primary-01"}] "Extract failures" "Last 24 hours" "Show extract failures in the last 24 hours"]
                                            [[:> ChartColumn {:size 20 :class "text-kleene-primary-v2 dark:text-kleene-primary-01"}] "Create a chart" "From any of my tables" "I'd like to create a chart. Can you help me choose the right table and decide what to visualise?"]
                                            [[:> ChartNoAxesCombined {:size 20 :class "text-kleene-primary-v2 dark:text-kleene-primary-01"}] "Create a report" "With charts & insights" "I'd like to create an analytics report with charts. Can you walk me through what you need from me?"]
                                            [[:> SearchCode {:size 20 :class "text-kleene-primary-v2 dark:text-kleene-primary-01"}] "Explain SQL" "Help me understand a query" "Help me understand a SQL query."]
                                            [[:> LayoutList {:size 20 :class "text-kleene-primary-v2 dark:text-kleene-primary-01"}] "Review transform" "Suggest improvements" "Review a transform and suggest changes"]]]
             [:button {:key msg
                       :class "group flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-violet-300 hover:shadow-md hover:bg-violet-50/40 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-violet-600 dark:hover:bg-violet-950/30 transition-all cursor-pointer"
                       :on-click (fn []
                                   (scroll-to-the-bottom)
                                   (send-message msg))}
              icon
              [:span {:class "text-xs font-semibold text-slate-700 dark:text-slate-200 group-hover:text-violet-700 dark:group-hover:text-violet-300 transition-colors"} title]
              [:span {:class "text-[10px] text-slate-400 dark:text-slate-500 leading-tight"} subtitle]])]
          [kai-default-notice]])

       (let [turns    (vec (response/turns (sort-by :timestamp messages)))
             last-idx (dec (count turns))]
         (into [:<>]
               (map-indexed
                (fn [idx turn]
                  (let [in-progress? (and loading? (= idx last-idx))
                        copy-text    (when-not in-progress? (response/copy-text turn))]
                    (cond-> (into [:<>]
                                  (map (fn [message] [render-message message send-message answer-confirmation loading?]))
                                  turn)
                      copy-text (conj [copy-response-button {:text copy-text}])))))
               turns))

       (let [sorted          (sort-by :timestamp messages)
             last-msg        (last sorted)
             last-applied    (->> sorted
                                  (filter #(and (= "transform_proposal" (:type %))
                                                (get-in % [:proposal :created-transform])))
                                  last)]
         (when (and (seq messages)
                    (not loading?)
                    (= "assistant" (:author last-msg))
                    (= "text" (:type last-msg))
                    (not (:partial last-msg)))
           [:div {:class "w-full flex justify-center pt-1 pb-2"}
            [suggestion-chips {:send-message       send-message
                               :last-text          (:text last-msg)
                               :created-transform  (get-in last-applied [:proposal :created-transform])}]]))]

      [:div {:class "shrink-0 mx-auto w-full max-w-screen-md"}
       [:div {:class "flex items-center gap-1.5 px-3 pt-1.5"}
        [:button
         {:class "rounded-full border border-input bg-background p-1 opacity-30 hover:opacity-100"
          :type "button"
          :title "Clear chat history"
          :on-click clear-chat}
         [:> BrushCleaning {:size 15}]]

        (let [sql-ctx-raw       @(re-frame/subscribe [:ai-chat.sql/editor-context])
              editor-dismissed? @(re-frame/subscribe [:ai-chat/editor-dismissed?])]
          (when (and sql-ctx-raw editor-dismissed?)
            [:button
             {:class "rounded-full border border-input bg-background p-1 opacity-30 hover:opacity-100 hover:text-violet-600 dark:hover:text-violet-300"
              :type "button"
              :title (str "Reattach " (:source sql-ctx-raw) " · " (:tab-name sql-ctx-raw))
              :on-click #(dispatch [:ai-chat/reattach-editor])}
             [:> Plug {:size 15}]]))

        [:div {:class "flex-1"}]

        [:button
         {:class "rounded-full border border-input bg-background p-1 opacity-30 hover:opacity-100"
          :title "Scroll to the bottom"
          :type "button"
          :on-click #(scroll-to-the-bottom)}
         [:> ChevronDown {:size 15}]]]

       [:form {:class "w-full px-2"
               :on-submit (fn [ev]
                            (.preventDefault ev)
                            (when-not (or loading? (clojure.string/blank? prompt))
                              (scroll-to-the-bottom)
                              (send-message prompt)
                              (set-prompt "")
                              (reset-input-height)))}

        [:label {:for "ai-chat" :class "sr-only"} "Type something..."]
        [:div
         {:class "max-w-3xl rounded-lg border border-slate-300 bg-slate-50 transition-none duration-0 focus-within:ring-2 focus-within:ring-blue-500 dark:border-slate-700 dark:bg-slate-900"}
         [:textarea
          {:id "ai-chat"
           :type "text"
           :value prompt
           :autocomplete "off"
           :autoFocus true
           :on-input (fn [e]
                       (let [el (js/document.getElementById "ai-chat")]
                         (jsi/assoc-in! el [:style :height] "auto")
                         ;; Need additional 2 px so that scrollbars don't appear
                         (jsi/assoc-in! el [:style :height] (str (+ 2 (min 120 (jsi/get-in el [:scrollHeight]))) "px"))))
           :on-key-down (fn [e]
                          (.stopPropagation e)
                          (when (and (= "Enter" (jsi/get e :key))
                                     (not (jsi/get e :shiftKey)))
                            (.preventDefault e)
                            (when-not (or loading? (clojure.string/blank? prompt))
                              (scroll-to-the-bottom)
                              (send-message prompt)
                              (set-prompt "")
                              (reset-input-height))))
           :on-change (fn [e]
                        (set-prompt (jsi/get-in e [:target :value])))
           :class "duration-0 h-auto w-full rounded-lg bg-transparent p-3 pb-1 text-sm text-slate-800 focus:outline-none dark:text-slate-200 resize-none"
           :placeholder "Ask anything..."
           :rows "1"
           :required ""}]
         [:div {:class "flex items-center justify-between gap-2 px-2 pb-1.5"}
          [:div {:class "flex items-center gap-1.5 min-w-0"}
           [context-badge
            (cond
              context {:name     (:name context)
                       :on-clear #(dispatch [:ai-chat/clear-context])}
              sql-ctx {:name     (:tab-name sql-ctx)
                       :on-clear #(dispatch [:ai-chat/dismiss-editor])})]
           [model-control]]
          (if loading?
            [:button
             {:type "button"
              :on-click abort-request
              :class "flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-kleene-primary-v2 border border-violet-300 bg-white hover:bg-violet-50 dark:bg-slate-900 dark:border-violet-700 dark:text-violet-400 dark:hover:bg-violet-950/40 transition-colors"
              :title "Stop generating"}
             [:> Loader2 {:size 13 :class "animate-spin opacity-50"}]
             [:> Square {:size 11 :class "fill-current"}]]

            [:button
             {:type "submit"
              :disabled loading?
              :class "shrink-0 rounded-lg p-1.5 text-sm text-slate-500 hover:text-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 sm:text-base"}
             [:> Send {:size 18}]])]]

        [:small {:class "block text-slate-500 px-2"} "Please verify all information independently, as AI may occasionally produce inaccurate or incomplete responses."]]]]]))

(def ^:private hide-under-overlay "[body:has([data-slot=dialog-overlay])_&]:z-[1900]")

(defn ai-button []
  [:div {:class ["h-[50px] w-[50px]"
                 "fixed right-[10px] bottom-[10px] z-[1900]"
                 "rounded-full flex items-center justify-center"
                 "cursor-pointer active:scale-[0.98]"]
         :on-click toggle-ai-modal}
   [:img {:src   "/img/kai/KAI-2.svg"
          :class "drop-shadow-sm/25"
          :title "Open KAI the AI"}]])

(defn- clamp-float-pos
  "Keeps the floating panel on-screen, its 44px header (the drag handle) included."
  [x y w vw vh]
  [(max 0 (min x (- vw w)))
   (max 0 (min y (- vh 44)))])

(defn ai-modal []
  (let [;; --- hooks (unconditional) ---
        [docked? set-docked!]  (react/useState true)
        [prompt set-prompt]    (react/useState "")
        [panel-w set-panel-w!] (react/useState 420)
        [panel-h set-panel-h!] (react/useState 600)
        [float-x set-float-x!] (react/useState nil)
        [float-y set-float-y!] (react/useState nil)
        [float-w set-float-w!] (react/useState 420)
        [float-h set-float-h!] (react/useState 620)
        h-resizing             (react/useRef false)   ;; left-edge → width (docked)
        v-resizing             (react/useRef false)   ;; top-edge → height (docked)
        f-resizing             (react/useRef false)   ;; corner → size (floating)
        f-resize-start         (react/useRef #js {:x 0 :y 0 :w 420 :h 620})
        dragging               (react/useRef false)
        drag-off               (react/useRef #js {:dx 0 :dy 0})
        chat-input-sel         (react/useRef nil)
        [viewport set-viewport!] (react/useState ui.modal/get-viewport-size)

        ;; horizontal resize (left edge → width, docked)
        _ (react/useEffect
           (fn []
             (let [on-move (fn [e]
                             (when (.-current h-resizing)
                               (let [w (- (.-innerWidth js/window) (.-clientX e))]
                                 (set-panel-w! (max 280 (min 900 w))))))
                   on-up   (fn [_] (set! (.-current h-resizing) false))]
               (.addEventListener js/window "mousemove" on-move)
               (.addEventListener js/window "mouseup" on-up)
               (fn []
                 (.removeEventListener js/window "mousemove" on-move)
                 (.removeEventListener js/window "mouseup" on-up))))
           #js [])

        ;; vertical resize (top edge → height, docked)
        _ (react/useEffect
           (fn []
             (let [on-move (fn [e]
                             (when (.-current v-resizing)
                               (let [h (- (.-innerHeight js/window) (.-clientY e))]
                                 (set-panel-h! (max 200 (min (* 0.95 (.-innerHeight js/window)) h))))))
                   on-up   (fn [_] (set! (.-current v-resizing) false))]
               (.addEventListener js/window "mousemove" on-move)
               (.addEventListener js/window "mouseup" on-up)
               (fn []
                 (.removeEventListener js/window "mousemove" on-move)
                 (.removeEventListener js/window "mouseup" on-up))))
           #js [])

        ;; drag handler (floating)
        _ (react/useEffect
           (fn []
             (let [on-move (fn [e]
                             (when (jsi/get dragging :current)
                               (let [{:keys [dx dy]} (jsi/lookup (jsi/get drag-off :current))]
                                 (set-float-x! (- (jsi/get e :clientX) dx))
                                 (set-float-y! (- (jsi/get e :clientY) dy)))))
                   on-up   (fn [_] (jsi/assoc! dragging :current false))]
               (.addEventListener js/window "mousemove" on-move)
               (.addEventListener js/window "mouseup" on-up)
               (fn []
                 (.removeEventListener js/window "mousemove" on-move)
                 (.removeEventListener js/window "mouseup" on-up))))
           #js [])

        _ (react/useEffect
           (fn []
             (let [vv        (jsi/get js/window :visualViewport)
                   on-resize (throttle #(set-viewport! (ui.modal/get-viewport-size)) 100)]
               (jsi/call js/window :addEventListener "resize" on-resize)
               (when vv (jsi/call vv :addEventListener "resize" on-resize))
               (fn []
                 (jsi/call js/window :removeEventListener "resize" on-resize)
                 (when vv (jsi/call vv :removeEventListener "resize" on-resize)))))
           #js [])

        ;; corner resize handler (floating)
        _ (react/useEffect
           (fn []
             (let [on-move (fn [e]
                             (when (.-current f-resizing)
                               (let [start (.-current f-resize-start)
                                     dx    (- (.-clientX e) (.-x start))
                                     dy    (- (.-clientY e) (.-y start))]
                                 (set-float-w! (max 320 (min 900 (+ (jsi/get start :w) dx))))
                                 (set-float-h! (max 300 (min (* 0.92 (jsi/get js/window :innerHeight)) (+ (jsi/get start :h) dy)))))))
                   on-up   (fn [_] (set! (.-current f-resizing) false))]
               (.addEventListener js/window "mousemove" on-move)
               (.addEventListener js/window "mouseup" on-up)
               (fn []
                 (.removeEventListener js/window "mousemove" on-move)
                 (.removeEventListener js/window "mouseup" on-up))))
           #js [])

        _ (react/useEffect
           (fn []
             (when-let [sel (.-current chat-input-sel)]
               (when-let [el (js/document.getElementById "ai-chat")]
                 (.focus el)
                 (try
                   (.setSelectionRange el (.-start sel) (.-end sel))
                   (catch :default _ nil)))
               (set! (.-current chat-input-sel) nil))
             js/undefined)
           #js [docked?])

        ;; --- subscriptions ---
        current-user  @(re-posh/subscribe [:user/pull])
        token         (:user/session-token current-user)
        open?         @(re-posh/subscribe [:modals.subs/modal-open? "ai-modal"])
        ai-chat       @(re-posh/subscribe [:ai-chat/messages])
        conversation-id @(re-frame/subscribe [:ai-chat/conversation-id])
        context       @(re-posh/subscribe [:ai-chat/context])
        sql-ctx-raw   @(re-frame/subscribe [:ai-chat.sql/editor-context])
        editor-dismissed? @(re-frame/subscribe [:ai-chat/editor-dismissed?])
        sql-ctx       (when-not editor-dismissed? sql-ctx-raw)
        loading?      @(re-posh/subscribe [:ai-chat/loading?])
        settings      @(re-posh/subscribe [:ai-settings/pull])
        chosen-model  @(re-frame/subscribe [:ai-chat/model])
        byol?         @(re-posh/subscribe [:unleash/is-enabled "fe.ai-byol"])
        _ (react/useEffect
           (fn []
             (when (and open? byol?) (dispatch [:ai-chat/get-settings]))
             js/undefined)
           #js [open? byol?])
        draft         @(re-frame/subscribe [:ai-chat/draft])
        ;; Seed the input from a one-shot draft (e.g. "debug this failure" from the
        ;; dependency-graph failures sidebar), then clear it so it applies once.
        _ (react/useEffect
           (fn []
             (when (not-empty draft)
               (set-prompt draft)
               (dispatch [:ai-chat/clear-draft]))
             js/undefined)
           #js [draft])
        panel-bottom  68

        ai-chat-id    :ai-chat

        abort-request (fn []
                        (dispatch [::o/abort ai-chat-id])
                        (dispatch [:ai-chat/set-loading false]))

        start-stream  (fn [payload on-error]
                        (dispatch [:ai-chat/set-loading true])
                        (dispatch [::o/sse-client
                                   {:id              ai-chat-id
                                    ;; Swap url for local testing
                                    :uri             (str API_HOST "/ai-chatbot/chat")
                                    ;:uri             "http://127.0.0.1:8000/ai-chatbot/chat"
                                    :auto-reconnect? false
                                    :fetch-options   {:method  "POST"
                                                      ;; Comment this out for local testing as well
                                                      :credentials "include"
                                                      :headers {:authorization (str "Bearer " token)
                                                                "Content-Type" "application/json"}
                                                      :body    (js/JSON.stringify (clj->js payload))}
                                    :on-event        [:ai-chat/on-event]
                                    :on-close        [:ai-chat/set-loading false]
                                    :on-error        on-error}]))

        base-payload  (fn [conv-id]
                        (let [model (byol/chosen-model settings chosen-model)]
                          (cond-> {"conversation_id" conv-id}
                            (some? model) (assoc "model" model))))

        answer-confirmation (fn [id confirmed?]
                              (when conversation-id
                                (dispatch [:ai-chat/mark-confirmation id (if confirmed? :confirmed :cancelled)])
                                (start-stream (confirmation/answer-payload (base-payload conversation-id) id confirmed?)
                                              [:ai-chat/confirmation-failed id])))

        send-message  (fn [message]
                        (let [focus    (cond
                                         (and sql-ctx (or (:master-id sql-ctx)
                                                          (not (clojure.string/blank? (:sql sql-ctx)))))
                                         (select-keys sql-ctx [:sql :master-id :group-id])

                                         context
                                         (select-keys context [:sql :master-id :group-id]))
                              focus-payload (cond-> {}
                                              (not (clojure.string/blank? (:sql focus)))
                                              (assoc "sql" (:sql focus))
                                              (:master-id focus) (assoc "master_id" (:master-id focus))
                                              (:group-id focus)  (assoc "group_id" (:group-id focus)))
                              conv-id (or conversation-id (str (random-uuid)))
                              model   (byol/chosen-model settings chosen-model)
                              byol-active (boolean (:ai-settings/byol-effective settings))
                              active  (when byol-active (byol/active-choice settings chosen-model))
                              payload (cond-> (assoc (base-payload conv-id) "prompt" message)
                                        (seq focus-payload)
                                        (assoc "entity_context" focus-payload))]
                          (when (nil? conversation-id)
                            (dispatch [:ai-chat/set-conversation-id conv-id]))
                          (dispatch [:ai-chat/add-user-message message])
                          (dispatch [:posthog/capture {:event (:kai/message-sent posthog-events/events)
                                                       :props {:model            (:model active)
                                                               :provider         (:provider active)
                                                               :byol_active      byol-active
                                                               :is_default_model (nil? model)}}])
                          (start-stream payload [:ai-chat/set-loading false])))

        ;; Automatically send prompts originating outside the AI modal (e.g. Overview Kai bar)
        pending-prompt @(re-frame/subscribe [:ai-chat/pending-prompt])
        _ (react/useEffect
           (fn []
             (when (and open? (not (clojure.string/blank? pending-prompt)))
               (send-message pending-prompt)
               (dispatch [:ai-chat/clear-pending-prompt]))
             (fn []
               (dispatch [:ai-chat/clear-pending-prompt])))
           #js [open? pending-prompt])]

    (useHotkeys "a>i" toggle-ai-modal)

    (when open?
      (let [{vw :width vh :height} viewport
            fw        (min float-w vw)
            fh        (min float-h (* 0.92 vh))
            [fx fy]   (clamp-float-pos (or float-x 80) (or float-y 80) fw vw vh)
            panel-header
            [:div {:class (str "flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 shrink-0"
                               (when-not docked? " cursor-move"))
                   :on-mouse-down (when-not docked?
                                    (fn [e]
                                      (when-not (jsi/call-in e [:target :closest] "button")
                                        ;; no text selection while dragging; blur since preventDefault also stops the focus change
                                        (jsi/call e :preventDefault)
                                        (some-> (jsi/get js/document :activeElement) (jsi/call :blur))
                                        (let [el   (jsi/get-in e [:currentTarget :parentElement])
                                              rect (jsi/call el :getBoundingClientRect)]
                                          (jsi/assoc! (jsi/get drag-off :current)
                                                      :dx (- (jsi/get e :clientX) (jsi/get rect :left))
                                                      :dy (- (jsi/get e :clientY) (jsi/get rect :top)))
                                          (jsi/assoc! dragging :current true)))))}
             [:img {:src "/img/kai/KAI-2.svg" :class "h-6 w-6 shrink-0"}]
             [:span {:class "text-sm font-semibold text-slate-800 dark:text-slate-100"} "KAI"]
             [:div {:class "flex-1"}]
             [:button {:class "rounded-md p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                       :title (if docked? "Switch to floating" "Dock to side")
                       :on-mouse-down (fn [e] (.preventDefault e))
                       :on-click (fn []
                                   (let [el (js/document.getElementById "ai-chat")]
                                     (when (and el (= el (.-activeElement js/document)))
                                       (set! (.-current chat-input-sel)
                                             #js {:start (.-selectionStart el)
                                                  :end   (.-selectionEnd el)})))
                                   (dispatch [:posthog/capture {:event (:kai/panel-mode-changed posthog-events/events)
                                                                :props {:mode (if docked? "floating" "docked")}}])
                                   (set-docked! (not docked?)))}
              (if docked? [:> Menu {:size 15}] [:> Anchor {:size 15}])]
             [:button {:class "rounded-md p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                       :title "Close"
                       :on-click (fn [] (dispatch [:modals.events/toggle-modal "ai-modal" false]))}
              [:> X {:size 15}]]]

            panel-content
            [:div {:class "flex-1 min-h-0 overflow-hidden"}
             [:f> render-messages {:send-message send-message
                                   :answer-confirmation answer-confirmation
                                   :messages ai-chat
                                   :context context
                                   :sql-ctx sql-ctx
                                   :loading? loading?
                                   :abort-request abort-request
                                   :open? open?
                                   :prompt prompt
                                   :set-prompt set-prompt}]]]

        [:div {:class "print:hidden"}
         (if docked?
           ;; ── Docked panel ──────────────────────────────────────────────
           [:div {:class ["fixed right-0 flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl z-[18000] rounded-t-xl overflow-hidden"
                          hide-under-overlay]
                  :style {:width  (str panel-w "px")
                          :height (str (max 200 (min panel-h (- vh panel-bottom))) "px")
                          :bottom (str panel-bottom "px")}}
            [:div {:class "absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-violet-400/30 transition-colors z-10"
                   :on-mouse-down (fn [_] (set! (.-current h-resizing) true))}]
            [:div {:class "absolute top-0 left-0 right-0 h-1.5 cursor-ns-resize hover:bg-violet-400/30 transition-colors z-10"
                   :on-mouse-down (fn [_] (set! (.-current v-resizing) true))}]
            panel-header
            panel-content]

           ;; ── Floating panel ────────────────────────────────────────────
           [:div {:class ["fixed flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl z-[18000] rounded-xl overflow-hidden"
                          hide-under-overlay]
                  :style {:left (str fx "px") :top (str fy "px")
                          :width (str fw "px") :height (str fh "px")}}
            panel-header
            panel-content
            ;; Resize grip — rendered last so it's highest in paint order.
            ;; Explicit z-[9999] and pointer-events-auto guarantee it's always on top.
            [:div {:class "absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize z-[9999]"
                   :style {:pointer-events "auto"}
                   :on-mouse-down (fn [e]
                                    (.stopPropagation e)
                                    (set! (.-x (.-current f-resize-start)) (.-clientX e))
                                    (set! (.-y (.-current f-resize-start)) (.-clientY e))
                                    (jsi/assoc! (jsi/get f-resize-start :current) :w fw :h fh)
                                    (set! (.-current f-resizing) true))}
             [:svg {:class "absolute bottom-1 right-1 opacity-40"
                    :width "12" :height "12" :viewBox "0 0 12 12"
                    :style {:pointer-events "none"}}
              [:path {:d "M2 10 L10 2 M6 10 L10 6 M10 10 L10 10"
                      :stroke "#64748b" :stroke-width "1.5" :stroke-linecap "round"}]]]])]))))
